<script>
import { fandomBasicMixin } from "src/modules/fandom_mixins_module.js";
import { pick, get } from "lodash";
import Flickity from "flickity";
import "magnific-popup";
import "magnific-popup/dist/magnific-popup.css";

export default {
  mixins: [fandomBasicMixin],
  mounted() {
    Vue.nextTick(() => {
      const procedure = $(this.$refs["procedure"]);
      if (procedure.length !== 0) {
        procedure.magnificPopup({type: "image", delegate: ".magnific-popup"});
      }
      if (Fandom.exists(this.content.accessories)) {
        new Flickity(this.$refs["accessory-carousel"], {
          draggable: ">1",
          prevNextButtons: false,
          pageDots: false,
          cellAlign: "left",
          wrapAround: false,
          freeSCroll: false,
          contain: true
        });
      }
    });
  },
  methods: {
    getBackgroundUrl: Fandom.getBackgroundUrl,
    getIngredientsTitle(customTitle) {
      if(Fandom.exists(customTitle)) {
        return customTitle;
      } else {
        return this.titleInfo;
      }
    },
    calcIngedients(ingredients) {
      return ingredients.map(ingredient => {
        const content = ingredient["ingredient"] ? this.getContent(ingredient["ingredient"]) : {};
        const title = get(ingredient, "title", "");
        return {
          quantity: get(ingredient, "quantity", ""),
          title: get(content, "title", title)
        };
      });
    },
    getTagsInfoFromContents(name, info = "title") {
      const field = get(this.content, name, []);
      const arrayConverter = content => Fandom.exists(content) ? pick(content, info) : false;
      const baseConverter = content => Fandom.exists(content) ? content[info] : false;
      const converter = Array.isArray(info) ? arrayConverter : baseConverter;
      return field.map(this.getContent).map(converter);
    }
  },
  computed: {
    id() {
      return Fandom.exists(this.content.id) ? this.content.id : this.content["$id"];
    },
    ingredientsBoxes() {
      const primaryBox = {
        ingredients: this.content.ingredients,
        allergens: this.content.allergens,
        title: this.content.ingredients_title
      };
      const secondaryBox = {
        ingredients: this.content.secondary_ingredients,
        allergens: this.content.secondary_allergens,
        title: this.content.secondary_ingredients_title
      };
      const additionalBoxes = get(this, ["content", "ingredient_boxes"], []);
      return [primaryBox, secondaryBox].concat(additionalBoxes).filter(box => Fandom.exists(box.ingredients));
    },
    accessories() {
      return this.getTagsInfoFromContents("accessories", ["slug","thumbnail","title"]);
    },
    titleInfo() {
      const title = this.ft("course.ingredients");
      const servings = get(this, ["content", "info", "quantity"], false);
      return servings ? `${title} ${this.ft("course.ingredients_for", { servings })}` : title;
    }
  }
};
</script>

<template>
  <div class="container pt-4 pb-3" v-easyadmin="easyadminId" :id="`ref-${easyadminId}`" :class="contentType" stripe-lesson-component>
    <div class="row justify-content-center">
      <div class="lesson-main-content align-self-start col-12 col-lg-8 col-xl-7 pl-lg-0 order-4 order-lg-3">
        <template v-if="content.title_composed || content.title">
          <div v-if="content.title_composed" class="title-composed py-lg-4 py-3 d-none d-lg-block">
            <h3 v-for="(titleLine, index) in content.title_composed" :key="`title-${index}`" :class="getTitleStyle(titleLine)" class="d-inline-block m-0 titleline">
              {{titleLine.title}}
            </h3>
          </div>
          <h4 class="pb-3 title-composed-fallback" v-else-if="content.title">{{content.title}}</h4>
        </template>
        <div class="row">
          <div class="equipment col-12" v-if="accessories.length > 0">
            <h4 class="pb-3">{{ft("course.equipment")}}</h4>
            <div class="accessory-carousel" ref="accessory-carousel">
              <div class="accessory-carousel-item border border-rhubarb" v-for="(accessory, index) in accessories" :key="`carousel-slide-${index}`">
                <a :href="accessory.slug">
                  <div v-if="accessory.thumbnail" class="w-100 h-100 position-absolute bg-cover-center" :style="{'background-image': getBackgroundUrl(accessory.thumbnail.url)}">
                  </div>
                </a>
              </div>
            </div>
          </div>
          <div v-if="content.steps" class="procedure pt-4 col-12" ref="procedure">
            <h4 class="pb-3">{{ft("course.procedure")}}</h4>
            <div v-for="(step, i) in content.steps" class="entire-step pb-4" :key="`step-${i}`">
              <div class="row">
                <div class="col-auto col-md-1 d-flex pr-2">
                  <div class="step-counter bg-rhubarb text-white d-flex rounded-circle align-items-center justify-content-center">{{i+1}}</div>
                </div>
                <template v-if="step.title">
                  <div class="col col-lg-11 d-flex align-items-center pl-2 step-title">{{step.title}}</div>
                  <div class="col-1 d-lg-flex pr-2 d-none"><div class="step-counter-mock-spacer"></div></div>
                </template>
                <div class="col-12 col-lg-11 d-flex flex-column align-items-begin pl-lg-2 pt-2">
                  <div v-html="step.description"></div>
                  <a v-if="step.image" class="recipe-img bg-cover-center magnific-popup" :href="step.image.url" :style="{'background-image': getBackgroundUrl(step.image.url)}" ></a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="ingredientsBoxes.length > 0" class="col-12 col-lg-4 col-xl-5 lesson-ingredients order-3 order-lg-4">
        <template v-if="content.title_composed || content.title">
          <div v-if="content.title_composed" class="title-composed py-lg-4 py-3 d-block d-lg-none">
            <h3 v-for="(titleLine, index) in content.title_composed" :key="`title-${index}`" :class="getTitleStyle(titleLine)" class="d-inline-block m-0 titleline">
              {{titleLine.title}}
            </h3>
          </div>
          <h4 class="pb-3 title-composed-fallback" v-else-if="content.title">{{content.title}}</h4>
        </template>
        <ingredients-box
          v-for="(box, index) in ingredientsBoxes"
          :key="`ingredient-box-${index}`"
          :content="content"
          :containerContent="containerContent"
          :title="getIngredientsTitle(box.title)"
          :ingredients="calcIngedients(box.ingredients)"
          :allergens="getTagsInfoFromContents(box.allergens)"
          :name="`ingredient-box-${index}`"
          color="rhubarb"
          >
        </ingredients-box>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$icon-size: 2.625rem;

.title-composed,
.title-composed-fallback {
  color: $kenwood-dark-grey;
  font-family: $bressay-font;
}

.titleline {
  & + & {
    margin-left: .5ch !important;
  }
}

.step-counter,
.step-counter-mock-spacer {
  width: $icon-size;
  height: $icon-size;
  font-family: $bressay-font;
  font-size: 1.75rem;
}

.recipe-img {
  width: 100%;
  padding-bottom: 52.5%;
  transition: opacity 0.35s;

  @include hover {
    opacity: 0.92;
  }
}

.accessory-carousel-item {
  width: 23.5%;
  padding-bottom: 23.5%;
  margin-right: 1.5%;

  @include media-breakpoint-down(sm) {
    width: 38.5%;
    padding-bottom: 38.5%;
    margin-right: 1.5%;
  }
}

.step-title {
  font-family: $bressay-font;
  font-size: 1.25rem;
}
</style>
